import posthog from "posthog-js"
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react"
import { Component, type ErrorInfo, type ReactNode, useEffect } from "react"
import { useLocation } from "react-router-dom"
import {
	AppErrorScreen,
	describeUnknownError,
	getErrorReportId
} from "~/components/screens/AppErrorScreen"
import { VERSION } from "~/config"
import { useOrganization } from "~/hooks/useOrganization"
import { useTeam } from "~/hooks/useTeam"
import { authClient } from "~/lib/auth-client"
import { api } from "~/trpc/react"

const getRuntimeConfig = () =>
	(
		globalThis as typeof globalThis & {
			__ORBIT_CONFIG__?: { posthogKey?: string; posthogHost?: string }
		}
	).__ORBIT_CONFIG__
const getPostHogKey = () =>
	getRuntimeConfig()?.posthogKey ?? import.meta.env.VITE_POSTHOG_KEY
const getPostHogHost = () =>
	getRuntimeConfig()?.posthogHost ??
	import.meta.env.VITE_POSTHOG_HOST ??
	"https://pogpig.buape.com"

export const posthogEnabled = Boolean(getPostHogKey())

export const captureFeatureFlagView = (flagKey: string) => {
	if (!posthogEnabled) return
	posthog.capture("$feature_view", { feature_flag: flagKey })
}

export const captureFeatureFlagInteraction = (flagKey: string) => {
	if (!posthogEnabled) return
	posthog.capture("$feature_interaction", { feature_flag: flagKey })
	posthog.setPersonProperties({ [`$feature_interaction/${flagKey}`]: true })
}

let posthogInitialized = false

export function initializePostHog() {
	const posthogKey = getPostHogKey()
	if (!posthogKey || posthogInitialized) return
	posthogInitialized = true
	posthog.init(posthogKey, {
		api_host: getPostHogHost(),
		ui_host: "https://us.posthog.com",
		defaults: "2026-01-30",
		person_profiles: "identified_only",
		capture_pageview: false,
		capture_exceptions: {
			capture_unhandled_errors: true,
			capture_unhandled_rejections: true,
			capture_console_errors: true
		},
		error_tracking: {
			exception_steps: {
				enabled: true,
				max_bytes: 32768
			}
		},
		capture_heatmaps: true,
		disable_session_recording: false,
		session_recording: {
			maskAllInputs: true
		},
		enable_recording_console_log: true,
		logs: {
			captureConsoleLogs: true,
			serviceName: "orbit-web",
			environment: import.meta.env.MODE,
			serviceVersion: VERSION
		},
		opt_in_site_apps: true
	})
}

export function PostHogProvider({ children }: { children: ReactNode }) {
	useEffect(() => {
		initializePostHog()
	}, [])

	const { data: sessionData } = authClient.useSession()

	useEffect(() => {
		if (posthogEnabled && sessionData?.user) {
			posthog.identify(sessionData.user.id, {
				email: sessionData.user.email,
				username: sessionData.user.name,
				role: sessionData.user.role,
				banned: sessionData.user.banned
			})
		}
	}, [sessionData])

	const { organization } = useOrganization()

	useEffect(() => {
		if (posthogEnabled && organization) {
			posthog.group("organization", organization.id, {
				name: organization.name,
				slug: organization.slug
			})
		}
	}, [organization])

	const { team } = useTeam()

	useEffect(() => {
		if (posthogEnabled && team) {
			posthog.group("team", team.id, {
				name: team.name,
				slug: team.slug,
				organization_id: team.organizationId
			})
		}
	}, [team])

	return (
		<PHProvider client={posthog}>
			<AppErrorBoundary>
				<PostHogPageView />
				<PresenceHeartbeat />
				{children}
			</AppErrorBoundary>
		</PHProvider>
	)
}

class AppErrorBoundary extends Component<
	{ children: ReactNode },
	{
		error: Error | null
		errorInfo: ErrorInfo | null
		exceptionEvent: unknown
	}
> {
	state: {
		error: Error | null
		errorInfo: ErrorInfo | null
		exceptionEvent: unknown
	} = {
		error: null,
		errorInfo: null,
		exceptionEvent: null
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		const exceptionEvent = posthogEnabled
			? posthog.captureException(error, {
					source: "react_error_boundary",
					component_stack: errorInfo.componentStack
				})
			: null

		this.setState({ error, errorInfo, exceptionEvent })
	}

	render() {
		if (this.state.error) {
			return (
				<AppErrorBoundaryFallback
					error={this.state.error}
					errorInfo={this.state.errorInfo}
					exceptionEvent={this.state.exceptionEvent}
				/>
			)
		}

		return this.props.children
	}
}

function getClientTimezone() {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone
	} catch {
		return undefined
	}
}

function PresenceHeartbeat() {
	const { data: sessionData } = authClient.useSession()
	const heartbeat = api.user.heartbeat.useMutation()

	useEffect(() => {
		if (!sessionData?.user) return

		const sendHeartbeat = () => {
			if (document.visibilityState === "hidden") return
			heartbeat.mutate({
				timezone: getClientTimezone()
			})
		}

		sendHeartbeat()
		const interval = window.setInterval(sendHeartbeat, 45_000)
		document.addEventListener("visibilitychange", sendHeartbeat)

		return () => {
			window.clearInterval(interval)
			document.removeEventListener("visibilitychange", sendHeartbeat)
		}
	}, [sessionData?.user, heartbeat.mutate])

	return null
}

function AppErrorBoundaryFallback({
	error,
	errorInfo,
	exceptionEvent
}: {
	error: Error
	errorInfo: ErrorInfo | null
	exceptionEvent: unknown
}) {
	const errorId = getErrorReportId(exceptionEvent)

	return (
		<AppErrorScreen
			summary="Something went wrong while rendering this page."
			errorId={errorId}
			details={[
				{
					label: "Boundary",
					value: "react_error_boundary"
				},
				{
					label: "Error",
					value: describeUnknownError(error)
				},
				{
					label: "React component stack",
					value: errorInfo?.componentStack
				},
				{
					label: "Diagnostic event",
					value: exceptionEvent
				}
			]}
		/>
	)
}

function PostHogPageView() {
	const location = useLocation()
	const posthog = usePostHog()

	useEffect(() => {
		if (!posthogEnabled || !posthog) return
		const url = `${window.location.origin}${location.pathname}${location.search}`
		posthog.capture("$pageview", { $current_url: url })
	}, [location.pathname, location.search, posthog])

	return null
}
